/* eslint-disable no-case-declarations */
/* eslint-disable react/jsx-no-constructed-context-values */
/*
 * Author M. Atoar Rahman
 * Title: Supplier Context
 * Description: Page for Supplier Context
 * Date: 15/03/2022
 */

import { createContext, useContext, useReducer, useState } from 'react';
import useAxiosPrivate from '../../../hooks/useAxiosPrivate';
import ToastifyService from '../../../services/ToastifyService';
import UrlService from '../../../services/UrlService';

// Initial State
const initialState = {
    suppliers: [],
};

// Reducer Function
const reducer = (state, action) => {
    switch (action.type) {
        case 'SUPPLIER_LIST':
            return {
                ...state,
                suppliers: action.payload,
            };
        case 'ADD_SUPPLIER':
            return {
                ...state,
                suppliers: [action.payload, ...state.suppliers],
            };
        case 'EDIT_SUPPLIER':
            const updateSupplier = action.payload;

            const updateSuppliers = state.suppliers.map((supplier) => {
                if (supplier.id === updateSupplier.id) {
                    return updateSupplier;
                }
                return supplier;
            });
            return {
                ...state,
                suppliers: updateSuppliers,
            };

        default:
            return state;
    }
};

// Create Context
const ContextAPI = createContext(initialState);

export function useSupplier() {
    return useContext(ContextAPI);
}

// Provider Component
export function SupplierProvider({ children }) {
    const [state, dispatch] = useReducer(reducer, initialState);
    const [errMsg, setErrMsg] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);

    // Dependency
    const axiosPrivate = useAxiosPrivate();

    // API Function: Fatch All Data
    const getAllData = async () => {
        try {
            const response = await axiosPrivate.get(UrlService.getAllSupplierUrl(), {
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            dispatch({
                type: 'SUPPLIER_LIST',
                payload: response?.data?.supplier_info,
            });
        } catch (err) {
            if (!err?.response) {
                console.log('Server Not Responding!');
            } else if (err.response?.status === 401) {
                console.log('Unauthorized!');
            } else {
                console.log('Data Not Found!');
            }
        }
    };

    // API Function: Add/Edit Data
    const addOrUodateData = async (data, id) => {
        try {
            const response = await axiosPrivate.post(
                id ? UrlService.updateSupplierUrl(id) : UrlService.saveSupplierUrl(),
                JSON.stringify(data),
                {
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }
            );
            // if (id != null) {
            //     ToastifyService.updateNotify('Data Update Successfull');
            // } else {
            //     ToastifyService.saveNotify('Data Create Successfull');
            // }
            await getAllData();
            setShowModal(true);
            setIsSubmitted(true);

            if (response?.data?.status === 'error') {
                ToastifyService.errorNotify(response?.data?.message);
                setIsSubmitted(false);
            } else if (id != null) {
                ToastifyService.updateNotify('Data Update Successfull');
            } else {
                ToastifyService.saveNotify('Data Create Successfull');
            }
        } catch (err) {
            if (!err?.response) {
                setErrMsg('Server Not Responding!');
            } else if (err.response?.status === 401) {
                setErrMsg('Unauthorized!');
            } else if (id) {
                setErrMsg('Data Update Failed!');
            } else {
                setErrMsg('Data Insert Failed!');
            }
            ToastifyService.errorNotify(errMsg);
            setIsSubmitted(false);
        }
    };

    // Add Supplier
    const addSupplier = (supplier) => {
        addOrUodateData(supplier);
        dispatch({
            type: 'ADD_SUPPLIER',
            payload: supplier,
        });
    };

    // Edit Supplier
    const editSupplier = (supplier, id) => {
        addOrUodateData(supplier, id);
        dispatch({
            type: 'EDIT_SUPPLIER',
            payload: supplier,
        });
    };

    return (
        <ContextAPI.Provider
            value={{
                suppliers: state.suppliers,
                getAllData,
                addSupplier,
                editSupplier,
                showModal,
                isSubmitted,
                setIsSubmitted,
            }}
        >
            {children}
        </ContextAPI.Provider>
    );
}

export default ContextAPI;
